import { default as React } from 'react';
import { useMemo } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/Replay';
import type { TaskStream } from '@backstage/plugin-scaffolder-react';
import { linkifyText } from './linkifyText';

/**
 * Default link to the SkyStage deployment troubleshooting guide (TechDocs).
 * Kept as a relative TechDocs path so it works across dev/prod environments.
 */
const TROUBLESHOOTING_GUIDE_URL =
  '/docs/default/component/skystage/deploy-internal-apps-user-guide/#troubleshooting';

/**
 * Maps a failed scaffolder step id to a human-friendly failure title and
 * explanation. Falls back to a generic message for unrecognised steps.
 */
function describeFailure(failedStepId: string | undefined): {
  title: string;
  explanation: string;
} {
  const id = (failedStepId ?? '').toLowerCase();

  if (id.includes('build') || id.includes('docker') || id.includes('image')) {
    return {
      title: 'Deployment Failed - Docker Image Build Error',
      explanation:
        'The Docker image could not be built. This is commonly caused by errors in the Dockerfile, a missing dependency, an invalid build command, or a failed package install. Review the build logs below for the exact step that failed.',
    };
  }

  if (id.includes('argo') || id.includes('deploy') || id.includes('sync')) {
    return {
      title: 'Deployment Failed - ArgoCD Sync Error',
      explanation:
        'The application image was built but could not be deployed to the cluster. This is commonly caused by an invalid manifest, a resource quota limit, or an ArgoCD sync failure. Review the logs below for details.',
    };
  }

  if (id.includes('repo') || id.includes('github') || id.includes('git')) {
    return {
      title: 'Deployment Failed - Repository Error',
      explanation:
        'A repository operation failed during deployment. This is commonly caused by missing permissions, an existing repository name, or a GitHub authorization issue. Review the logs below for details.',
    };
  }

  return {
    title: 'Deployment Failed',
    explanation:
      'The application was deployed but failed to start up. This is commonly caused by an error in the application code, a missing secret or environment variable, or exceeding the container resource limits. Review the pod crash logs and the troubleshooting guide below.',
  };
}

/**
 * Returns the id of the first failed step in the task stream, if any.
 */
function findFailedStepId(taskStream: TaskStream): string | undefined {
  const failedStep = Object.values(taskStream.steps ?? {}).find(
    step => step.status === 'failed',
  );
  return failedStep?.id;
}

/**
 * Returns true when the error is a transient network / connectivity issue
 * caused by the EventSource (SSE) log-streaming connection dropping. These
 * errors are set by Backstage's `useTaskEventStream` hook, which marks the
 * task as `completed` with an error while it retries in the background
 * (every 15 s, up to 3 retries). Because the task is still running on the
 * backend, these must NOT be treated as terminal deployment failures.
 *
 * @see useTaskEventStream in @backstage/plugin-scaffolder-react
 */
function isTransientNetworkError(error: Error | undefined): boolean {
  if (!error) return false;
  const msg = error.message?.toLowerCase() ?? '';
  return (
    msg.includes('failed to fetch') ||
    msg.includes('load failed') ||
    msg.includes('network error') ||
    msg.includes('network request failed') ||
    msg.includes('networkerror') ||
    msg.includes('we cannot connect at the moment') ||
    msg.includes('trying again') ||
    // "Retrying (1/3 retries)" — exact fragment from useTaskEventStream retry message
    msg.includes('retrying (') ||
    msg.includes('aborterror') ||
    msg.includes('err_network') ||
    msg.includes('err_connection') ||
    msg.includes('err_quic_protocol_error') ||
    msg.includes('net::err') ||
    msg.includes('econnrefused') ||
    msg.includes('econnreset') ||
    msg.includes('etimedout') ||
    msg.includes('epipe') ||
    msg.includes('socket hang up') ||
    // "Failed to read from EventStream" / "streamlogs connection lost" — SSE pipe errors
    msg.includes('eventstream') ||
    msg.includes('streamlogs')
  );
}

/**
 * Returns true only for a TERMINAL deployment failure — a step actually failed,
 * the task completed with a genuine (non-network) error, or the ArgoCD
 * status-check step completed successfully but the deployment itself failed
 * (signalled via markdown output).
 *
 * The ArgoCD status-check action never throws when a deployment fails — it
 * reports the failure through `deploymentStatusMarkdown` and exits cleanly so
 * that Backstage can still display the output links and the status text. All
 * four steps therefore show green even on a failed deployment. We detect this
 * case by scanning the Deployment Status markdown for the failure marker once
 * the task has completed.
 *
 * Backstage's `useTaskEventStream` sets `completed=true` and `error` when the
 * SSE log-streaming connection drops (network error), even though the backend
 * task is still running and the hook will retry in 15 seconds. We exclude
 * transient network errors from the terminal-failure check to prevent a
 * false-positive "Deployment Failed" alert mid-run (IDP-1100).
 */
export function isTerminalTaskFailure(taskStream: TaskStream): boolean {
  if (taskStream.cancelled) {
    return false;
  }

  const hasFailedStep = Object.values(taskStream.steps ?? {}).some(
    step => step.status === 'failed',
  );
  if (hasFailedStep) {
    return true;
  }

  // When the task is "completed" with an error, check whether the error is a
  // transient network issue from the SSE stream. If so, the backend task is
  // likely still running and the hook will retry — do NOT treat as terminal.
  if (Boolean(taskStream.completed) && Boolean(taskStream.error)) {
    if (isTransientNetworkError(taskStream.error)) {
      return false;
    }
    return true;
  }

  // ArgoCD failure path — all steps green, failure reported via markdown text.
  if (!taskStream.completed) {
    return false;
  }
  const textOutput = taskStream.output?.text as
    | Array<{ title: string; content: string }>
    | undefined;
  const mdContent = textOutput?.[0]?.content ?? '';
  // Both markers trigger the failure alert:
  //   '❌ Deployment Failed'     — infrastructure failure (ArgoCD, manifest errors)
  //   '❌ App Crashed on Startup' — pod started but app code crashed at runtime
  return (
    mdContent.includes('❌ Deployment Failed') ||
    mdContent.includes('App Crashed on Startup')
  );
}

/**
 * Smoothly scrolls the user to the first log/accordion section on the task page
 * so they can read the full build output. Falls back to scrolling to the bottom
 * of the page if no log container is found.
 */
function scrollToLogs(): void {
  const logTarget =
    document.querySelector('.MuiAccordion-root') ??
    document.querySelector('[class*="LogViewer"]') ??
    document.querySelector('pre');

  if (logTarget) {
    logTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } else {
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  }
}

export interface DeploymentFailureAlertProps {
  taskStream: TaskStream;
  /** Link to restart the deployment (template create page). */
  startOverUrl: string;
}

/**
 * Prominent error alert rendered at the top of the scaffolder task page when a
 * deployment fails. Surfaces a clear failure title, a brief explanation, and
 * actionable links (view logs, troubleshooting guide, start over) so users do
 * not have to dig through the log accordion to discover the failure.
 *
 * Implements IDP-996.
 */
export const DeploymentFailureAlert = ({
  taskStream,
  startOverUrl,
}: DeploymentFailureAlertProps) => {
  const failedStepId = useMemo(
    () => findFailedStepId(taskStream),
    [taskStream],
  );

  const hasFailed = isTerminalTaskFailure(taskStream);

  // When pod crash logs are present the deployment infrastructure succeeded —
  // the cluster started the pod but the application code threw an error at
  // runtime. Show a distinct title and advice to fix the code and redeploy
  // rather than treating it as a full deployment failure.
  const isRuntimeCrash = !!(taskStream.output?.podLogsContent as
    | string
    | undefined);

  const { title, explanation } = useMemo(() => {
    if (isRuntimeCrash) {
      return {
        title: 'App Crashed on Startup',
        explanation:
          'The infrastructure deployment completed successfully — your image was built, pushed, and applied to the cluster. However, the application crashed on startup. This is a code issue, not a deployment failure. Review the pod crash logs below, fix the error in your code, push your changes, then redeploy.',
      };
    }
    return describeFailure(failedStepId);
  }, [isRuntimeCrash, failedStepId]);

  if (!hasFailed) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }} data-testid="deployment-failure-alert">
      <Alert severity="error" variant="filled">
        <AlertTitle>{title}</AlertTitle>
        <Typography variant="body2" sx={{ mb: 1, opacity: 0.92 }}>
          {explanation}
        </Typography>
        {taskStream.error?.message && (
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontFamily: 'monospace',
              wordBreak: 'break-word',
              opacity: 0.85,
            }}
          >
            {linkifyText(taskStream.error.message)}
          </Typography>
        )}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ mt: 1 }}
        >
          <Button
            size="small"
            color="inherit"
            variant="outlined"
            startIcon={<DescriptionOutlinedIcon />}
            onClick={scrollToLogs}
          >
            View Full Build Logs
          </Button>
          <Button
            size="small"
            color="inherit"
            variant="outlined"
            startIcon={<MenuBookOutlinedIcon />}
            component={Link}
            href={TROUBLESHOOTING_GUIDE_URL}
          >
            Troubleshooting Guide
          </Button>
          <Button
            size="small"
            color="inherit"
            variant="outlined"
            startIcon={<ReplayOutlinedIcon />}
            component={Link}
            href={startOverUrl}
          >
            {isRuntimeCrash ? 'Redeploy' : 'Start Over'}
          </Button>
        </Stack>
      </Alert>
    </Box>
  );
};
